/**
 * MemoPopupOverlay Component
 * Full-screen centered popup for new memo notifications.
 */

import { useState } from 'react';
import { CloseIcon } from '@components/common/Icons';
import { ImageLightbox } from '@components/common/ImageLightbox';
import { getAttachmentUrl, isImageAttachment } from '../../../utils/attachmentUtils';
import type { Notification } from '../../../types';
import styles from './MemoPopupOverlay.module.css';

interface MemoPopupOverlayProps {
  notification: Notification;
  onClose: () => void;
}

export const MemoPopupOverlay = ({ notification, onClose }: MemoPopupOverlayProps) => {
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  const attachments = notification.attachments ?? [];
  const imageAttachments = attachments.filter(isImageAttachment);

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="memo-popup-title">
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 id="memo-popup-title" className={styles.title}>{notification.title}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close (mark as read)"
          >
            <CloseIcon size={24} />
          </button>
        </div>
        <div
          className={styles.body}
          dangerouslySetInnerHTML={{ __html: notification.body }}
        />
        {imageAttachments.length > 0 && (
          <div className={styles.attachments}>
            {imageAttachments.map((path) => (
              <img
                key={path}
                src={getAttachmentUrl(path)}
                alt="Memo attachment"
                className={styles.attachmentImage}
                onClick={() => setEnlargedImage(getAttachmentUrl(path))}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setEnlargedImage(getAttachmentUrl(path))}
              />
            ))}
          </div>
        )}
      </div>
      {enlargedImage && (
        <ImageLightbox
          src={enlargedImage}
          alt="Memo attachment"
          onClose={() => setEnlargedImage(null)}
        />
      )}
    </div>
  );
};
